import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass, ArrowClockwise, Trash, Envelope, Phone, CheckCircle, X, ArrowBendUpLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function AdminMessages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_messages")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-messages")
      .on("postgres_changes", { event: "*", schema: "public", table: "contact_messages" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (filter === "unread" && m.is_read) return false;
      if (filter === "replied" && !m.replied) return false;
      if (!query) return true;
      return `${m.name || ""} ${m.email || ""} ${m.subject || ""} ${m.message || ""}`.toLowerCase().includes(query);
    });
  }, [rows, q, filter]);

  const markRead = async (m) => {
    const { error } = await supabase.from("contact_messages").update({ is_read: true }).eq("id", m.id);
    if (!error) load();
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    const { error } = await supabase.from("contact_messages").delete().eq("id", m.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); setSelected(null); load(); }
  };

  const openMessage = (m) => {
    setSelected(m);
    if (!m.is_read) markRead(m);
  };

  return (
    <AdminLayout title="Messages">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-10 rounded-xl bg-white" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-10 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} className="rounded-xl h-10 border-slate-200">
          <ArrowClockwise size={14} weight="bold" className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[0,1,2].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No messages found.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((m) => (
              <button key={m.id} onClick={() => openMessage(m)} className="w-full text-left p-5 hover:bg-slate-50/60 transition-colors flex items-start gap-4">
                <div className={`h-10 w-10 rounded-full grid place-items-center text-white font-display font-bold ${m.is_read ? "bg-slate-300" : "bg-cta-gradient"}`}>
                  {(m.name || m.email || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-4">
                    <div className={`font-display font-semibold text-navy-deep ${!m.is_read && "font-bold"}`}>{m.name || m.email}</div>
                    <div className="text-[11px] text-slate-500 font-mono-data shrink-0">{fmtDate(m.created_at)}</div>
                  </div>
                  <div className="text-[12px] text-slate-500 mb-1 truncate">{m.email}</div>
                  {m.subject && <div className="text-[13px] font-semibold text-navy-deep">{m.subject}</div>}
                  <div className="text-[13px] text-slate-600 line-clamp-1">{m.message}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  {!m.is_read && <span className="h-2 w-2 rounded-full bg-cyan-brand" />}
                  {m.replied && <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-mono-data font-semibold">Replied</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-navy-deep/40 backdrop-blur-sm flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-mono-data text-cyan-brand font-semibold">Message</div>
                <h3 className="font-display font-bold text-navy-deep">{selected.subject || "No subject"}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="h-9 w-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"><X size={16} weight="bold" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><Envelope size={14} className="text-cool-grey" /><a href={`mailto:${selected.email}`} className="text-cyan-brand hover:underline">{selected.email}</a></div>
                {selected.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-cool-grey" />{selected.phone}</div>}
                <div className="text-[11px] text-slate-500 font-mono-data">Received {fmtDate(selected.created_at)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data mb-2">Message</div>
                <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.message}</div>
              </div>
              <div className="pt-4 border-t border-slate-200 flex justify-between">
                <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Aeonyx enquiry")}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-cta-gradient text-white font-semibold text-sm">
                  <ArrowBendUpLeft size={13} weight="bold" /> Reply
                </a>
                <div className="flex gap-2">
                  {!selected.replied && (
                    <Button variant="outline" onClick={async () => {
                      const { error } = await supabase.from("contact_messages").update({ replied: true }).eq("id", selected.id);
                      if (!error) { toast.success("Marked replied"); setSelected({ ...selected, replied: true }); load(); }
                    }} className="rounded-full h-10 border-slate-200">
                      <CheckCircle size={13} weight="bold" className="mr-1.5" /> Mark replied
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
